import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Lightbulb, Camera, Monitor, Eye, MapPin } from 'lucide-react';

const SmartTemple = () => {
  const deployedSystems = [
    {
      icon: Lightbulb,
      title: 'Facade Lighting',
      description: 'Intelligent LED lighting systems that illuminate the temple architecture with programmable scenes, creating stunning visual displays while optimizing energy consumption.',
      features: [
        'Programmable lighting scenes for festivals and special occasions',
        'Energy-efficient LED technology',
        'Remote control and scheduling',
        'Color temperature adjustment for different times of day'
      ]
    },
    {
      icon: Lightbulb,
      title: 'Smart Street Lights',
      description: 'IoT-enabled street lighting infrastructure providing enhanced safety for devotees while reducing operational costs through intelligent dimming and automated controls.',
      features: [
        'Motion sensor-based adaptive brightness',
        'Remote monitoring and fault detection',
        'Scheduled dimming during low-traffic hours',
        'Integration with central monitoring system'
      ]
    },
    {
      icon: Monitor,
      title: 'Command and Control Center',
      description: 'Centralized nerve center monitoring all temple operations, crowd movement, and security systems in real-time for enhanced devotee safety and operational efficiency.',
      features: [
        'Real-time monitoring of all integrated systems',
        'Centralized dashboard for temple administration',
        'Incident management and response coordination',
        'Analytics and reporting for better decision-making'
      ]
    },
    {
      icon: Camera,
      title: 'Video Management Software',
      description: 'Comprehensive surveillance solution managing multiple camera feeds across temple premises, ensuring complete visibility and rapid incident response.',
      features: [
        'Multi-camera feed management',
        'Recording and playback capabilities',
        'Integration with analytics software',
        'Secure storage and archival'
      ]
    },
    {
      icon: Eye,
      title: 'Video Analytics with Face Recognition',
      description: 'AI-powered video analytics providing intelligent crowd monitoring, VIP recognition, and security threat detection for enhanced safety management.',
      features: [
        'Real-time face recognition for VIP devotees',
        'Crowd density monitoring and alerts',
        'Suspicious behavior detection',
        'People counting and flow analysis'
      ]
    },
    {
      icon: Camera,
      title: 'ANPR (Automatic Number Plate Recognition)',
      description: 'Automated vehicle tracking system managing parking areas and identifying authorized vehicles for seamless temple operations and security.',
      features: [
        'Automatic vehicle identification at entry/exit',
        'Parking management and guidance',
        'Blacklist/whitelist vehicle monitoring',
        'Integration with access control systems'
      ]
    }
  ];

  return (
    <div className="detail-page">
      <div className="detail-header-section">
        <div className="detail-header-overlay"></div>
        <img
          src="https://images.pexels.com/photos/326699/pexels-photo-326699.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Yadadri Temple"
          className="detail-header-image"
        />
        <div className="detail-header-content">
          <Link to="/" className="back-button-detail">
            <ArrowLeft size={20} />
            Back to Home
          </Link>
          <h1 className="detail-title">Smart Temple</h1>
          <p className="detail-subtitle">
            Yadadri Temple - Preserving heritage through intelligent technology integration
          </p>
        </div>
      </div>

      <div className="detail-container">
        <section className="detail-section">
          <h2 className="detail-section-title">Project Overview</h2>
          <div className="project-overview">
            <p className="project-description">
              The Yadadri Temple smart transformation project represents a pioneering initiative in integrating 
              cutting-edge technology with spiritual heritage. Deployed across the temple premises with an 
              on-premise data center, this comprehensive solution enhances devotee experience, ensures safety, 
              and optimizes temple operations while respecting the sanctity of the religious environment.
            </p>
            <div className="project-highlights">
              <div className="highlight-item">
                <MapPin className="highlight-icon" size={24} />
                <div>
                  <h4>Location</h4>
                  <p>Yadadri Temple, Telangana</p>
                </div>
              </div>
              <div className="highlight-item">
                <Monitor className="highlight-icon" size={24} />
                <div>
                  <h4>Infrastructure</h4>
                  <p>On-premise Data Center</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="detail-section">
          <h2 className="detail-section-title">Deployed Systems</h2>
          <div className="systems-grid">
            {deployedSystems.map((system, index) => (
              <div key={index} className="system-card">
                <div className="system-header">
                  <div className="system-icon-wrapper">
                    <system.icon size={28} />
                  </div>
                  <h3 className="system-title">{system.title}</h3>
                </div>
                <p className="system-description">{system.description}</p>
                <ul className="system-features">
                  {system.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section className="detail-section">
          <h2 className="detail-section-title">Key Outcomes</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <h4>Enhanced Devotee Safety</h4>
              <p>Comprehensive surveillance and crowd monitoring ensure safe darshan experience for thousands of devotees daily.</p>
            </div>
            <div className="benefit-card">
              <h4>Improved Operations</h4>
              <p>Centralized command center enables efficient management of temple activities and rapid incident response.</p>
            </div>
            <div className="benefit-card">
              <h4>Energy Efficiency</h4>
              <p>Smart lighting systems reduce energy consumption while creating beautiful illumination of the temple architecture.</p>
            </div>
            <div className="benefit-card">
              <h4>Data-Driven Insights</h4>
              <p>Analytics from video and crowd monitoring help optimize resource allocation and improve devotee experience.</p>
            </div>
          </div>
        </section>

        <section className="detail-section">
          <h2 className="detail-section-title">Technical Architecture</h2>
          <div className="architecture-content">
            <p>
              All systems are integrated through a secure on-premise data center, ensuring data sovereignty 
              and compliance with religious institution requirements. The architecture supports:
            </p>
            <ul className="architecture-features">
              <li>Real-time data processing from IoT sensors and cameras</li>
              <li>Secure storage and retrieval of surveillance footage</li>
              <li>AI/ML processing for face recognition and analytics</li>
              <li>Centralized monitoring and control dashboard</li>
              <li>Redundant systems for 24/7 uptime</li>
              <li>Scalable infrastructure for future expansion</li>
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SmartTemple;
